import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { saveAs } from "file-saver";

/**
 * PDF pipeline — JS-measured page buckets (NOT CSS print / Puppeteer).
 *
 * Previous approach (tall document + translateY + overflow:hidden clips) left
 * mid-line slices whenever pad math and capture clip height disagreed by even
 * a few pixels. This version never slices a continuous canvas:
 *
 * 1. Measure blocks at the PDF content width.
 * 2. Place each block onto a discrete page div (whole block, or line-split
 *    with a min-lines widow/orphan rule).
 * 3. html2canvas each page div independently → jsPDF.
 */

const PARA_GAP_PX = 12;
const MIN_LINES = 3;

const prepareHTMLForPDF = (html) => {
  const tempElement = document.createElement("div");
  tempElement.innerHTML = html || "";

  tempElement.querySelectorAll("p").forEach((p) => {
    if (!p.textContent?.trim() && !p.querySelector("br,img")) {
      p.innerHTML = "<br>";
    }
  });

  tempElement
    .querySelectorAll("p, li, h1, h2, h3, h4, h5, h6, div, blockquote")
    .forEach((el) => {
      el.style.removeProperty("margin");
      el.style.removeProperty("margin-top");
      el.style.removeProperty("margin-bottom");
      el.style.removeProperty("margin-left");
      el.style.removeProperty("margin-right");
      el.style.removeProperty("padding");
      el.style.removeProperty("padding-top");
      el.style.removeProperty("padding-bottom");
    });

  return tempElement.innerHTML.trim() || "<p><br></p>";
};

const A4_MM = { width: 210, height: 297 };
const MM_K = 72 / 25.4;
const mmToPx = (mm) => Math.floor((mm * MM_K) / 72 * 96);

const PDF_MARGIN_MM = [14, 14, 14, 14];
const PDF_SCALE = 2;

export const getPdfPageMetrics = (margin = PDF_MARGIN_MM, scale = PDF_SCALE) => {
  const innerWidthMm = A4_MM.width - margin[1] - margin[3];
  const innerHeightMm = A4_MM.height - margin[0] - margin[2];
  const innerWidthPx = mmToPx(innerWidthMm);
  const pageHeightPx = mmToPx(innerHeightMm);
  return {
    margin,
    scale,
    innerWidthMm,
    innerHeightMm,
    innerWidthPx,
    pageHeightPx,
    sliceHeightPx: pageHeightPx,
    paraGapPx: PARA_GAP_PX,
    minLines: MIN_LINES,
  };
};

const PDF_ROOT_CSS = `
  .pdf-root, .pdf-page {
    box-sizing: border-box;
    background: #FFFEFD;
    color: #333;
    font-size: 12pt;
    line-height: 1.75;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  /* Flex + gap = one uniform spacing source. No margin-top/bottom on blocks. */
  .pdf-page {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    gap: ${PARA_GAP_PX}px;
  }
  .pdf-root {
    display: flex;
    flex-direction: column;
    gap: ${PARA_GAP_PX}px;
  }
  .pdf-page p,
  .pdf-page li,
  .pdf-page blockquote,
  .pdf-page h1,
  .pdf-page h2,
  .pdf-page h3,
  .pdf-page h4,
  .pdf-page h5,
  .pdf-page h6,
  .pdf-page div,
  .pdf-root p,
  .pdf-root li,
  .pdf-root blockquote,
  .pdf-root h1,
  .pdf-root h2,
  .pdf-root h3,
  .pdf-root h4,
  .pdf-root h5,
  .pdf-root h6,
  .pdf-root div {
    display: block;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.75;
  }
  .pdf-page h1, .pdf-page h2, .pdf-page h3, .pdf-page h4, .pdf-page h5, .pdf-page h6,
  .pdf-root h1, .pdf-root h2, .pdf-root h3, .pdf-root h4, .pdf-root h5, .pdf-root h6 {
    line-height: 1.4;
  }
  .pdf-page img, .pdf-page table,
  .pdf-root img, .pdf-root table {
    max-width: 100%;
  }
`;

const locateTextPosition = (root, targetOffset) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let remaining = targetOffset;
  let node = walker.nextNode();
  while (node) {
    const len = node.nodeValue.length;
    if (remaining <= len) return { node, offset: remaining };
    remaining -= len;
    node = walker.nextNode();
  }
  return null;
};

const snapOffsetToWord = (text, offset) => {
  if (offset <= 0) return 0;
  if (offset >= text.length) return text.length;
  if (/\s/.test(text[offset - 1])) return offset;
  let i = offset;
  while (i > 0 && !/\s/.test(text[i - 1])) i -= 1;
  return i > 0 ? i : offset;
};

const splitElementAtOffset = (el, offset) => {
  const start = locateTextPosition(el, offset);
  if (!start || offset <= 0) return offset <= 0 ? el : null;
  if (offset >= (el.textContent?.length || 0)) return null;

  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEndAfter(el.lastChild || el);
  const extracted = range.extractContents();
  if (!extracted || !extracted.textContent?.length) return null;

  const after = el.cloneNode(false);
  after.appendChild(extracted);
  return after;
};

const findMaxFittingOffset = (el, containerTop, maxBottom) => {
  const text = el.textContent || "";
  if (!text.length) return 0;
  const startPos = locateTextPosition(el, 0);
  if (!startPos) return 0;

  let lo = 0;
  let hi = text.length;
  let best = 0;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const endPos = locateTextPosition(el, mid);
    if (!endPos) {
      hi = mid - 1;
      continue;
    }
    const range = document.createRange();
    range.setStart(startPos.node, startPos.offset);
    range.setEnd(endPos.node, endPos.offset);
    const rects = range.getClientRects();
    const bottom = rects.length
      ? rects[rects.length - 1].bottom - containerTop
      : 0;
    if (bottom <= maxBottom) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return snapOffsetToWord(text, best);
};

const getLineBoxes = (el, containerTop) => {
  const range = document.createRange();
  try {
    range.selectNodeContents(el);
  } catch (_) {
    return [];
  }
  const rects = [...range.getClientRects()].filter(
    (r) => r.width > 0 && r.height > 0
  );
  const lines = [];
  for (const r of rects) {
    const top = r.top - containerTop;
    const bottom = r.bottom - containerTop;
    const last = lines[lines.length - 1];
    if (!last || Math.abs(last.top - top) > 1) {
      lines.push({ top, bottom });
    } else {
      last.bottom = Math.max(last.bottom, bottom);
      last.top = Math.min(last.top, top);
    }
  }
  return lines;
};

const blockExtent = (el, pageTop) => {
  const r = el.getBoundingClientRect();
  // Border-box only. Do NOT add marginBottom into the page-capacity check —
  // trailing margin is empty space and was causing false overflows that pushed
  // whole paragraphs to the next page (huge empty bottoms).
  return {
    top: r.top - pageTop,
    bottom: r.bottom - pageTop,
    height: r.height,
  };
};

const pageUsedHeight = (page) => {
  const pageTop = page.getBoundingClientRect().top;
  let max = 0;
  for (const child of page.children) {
    if (child.tagName === "STYLE") continue;
    max = Math.max(max, blockExtent(child, pageTop).bottom);
  }
  return max;
};

const isLeafTextBlock = (el) => {
  if (/^(P|LI|H1|H2|H3|H4|H5|H6|BLOCKQUOTE)$/i.test(el.tagName)) return true;
  if (/^(IMG|TABLE)$/i.test(el.tagName)) return true;
  if (el.tagName === "DIV") {
    const kids = [...el.children];
    return !kids.some((c) =>
      /^(P|LI|DIV|TABLE|UL|OL|H1|H2|H3|H4|H5|H6|BLOCKQUOTE)$/i.test(c.tagName)
    );
  }
  return false;
};

const collectBlocks = (root) => {
  const blocks = [];
  const walk = (parent) => {
    for (const child of [...parent.children]) {
      if (child.tagName === "STYLE") continue;
      if (isLeafTextBlock(child)) {
        blocks.push(child);
      } else if (child.children?.length) {
        walk(child);
      } else if (child.textContent?.trim()) {
        blocks.push(child);
      }
    }
  };
  walk(root);
  return blocks;
};

const createPageEl = (metrics) => {
  const page = document.createElement("div");
  page.className = "pdf-page";
  page.style.cssText = [
    `width:${metrics.innerWidthPx}px`,
    `height:${metrics.pageHeightPx}px`,
    "box-sizing:border-box",
    "position:relative",
    "overflow:visible",
    "background:#FFFEFD",
    "margin:0",
    "padding:0",
  ].join(";");
  return page;
};

const injectPdfStyles = (parent) => {
  const style = document.createElement("style");
  style.textContent = PDF_ROOT_CSS;
  parent.appendChild(style);
  return style;
};

const contentChildren = (page) =>
  [...page.children].filter((c) => c.tagName !== "STYLE");

/**
 * Slice `block` so the first piece's border-box height is <= maxHeightPx.
 * Returns the continuation element, or null if it can't split.
 * Mutates `block` into the first piece when successful.
 */
const splitBlockToHeight = (block, maxHeightPx, probeParent) => {
  if (maxHeightPx < 16) return null;

  probeParent.appendChild(block);
  void probeParent.offsetHeight;
  const probeTop = probeParent.getBoundingClientRect().top;
  const fullHeight = blockExtent(block, probeTop).height;

  if (fullHeight <= maxHeightPx + 0.5) {
    probeParent.removeChild(block);
    return null;
  }

  const lines = getLineBoxes(block, probeTop);
  if (lines.length < 2) {
    probeParent.removeChild(block);
    return null;
  }

  let keep = 0;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].bottom <= maxHeightPx + 0.5) keep = i + 1;
    else break;
  }

  if (keep <= 0) {
    probeParent.removeChild(block);
    return null;
  }

  // Soft widow preference only — never refuse to fill the page.
  let rest = lines.length - keep;
  if (rest > 0 && rest < MIN_LINES && keep > MIN_LINES) {
    const shifted = keep - (MIN_LINES - rest);
    if (shifted >= 1) keep = shifted;
  }

  if (keep >= lines.length) {
    probeParent.removeChild(block);
    return null;
  }

  while (keep >= 1) {
    const offset = findMaxFittingOffset(
      block,
      probeTop,
      lines[keep - 1].bottom
    );
    if (offset <= 0 || offset >= (block.textContent || "").length) {
      keep -= 1;
      continue;
    }

    const after = splitElementAtOffset(block, offset);
    if (!after) {
      keep -= 1;
      continue;
    }

    void probeParent.offsetHeight;
    const headHeight = blockExtent(block, probeTop).height;
    if (headHeight <= maxHeightPx + 0.5) {
      probeParent.removeChild(block);
      return after;
    }

    while (after.firstChild) block.appendChild(after.firstChild);
    keep -= 1;
  }

  probeParent.removeChild(block);
  return null;
};

/**
 * Discrete page packing. Always fills remaining space by splitting at line
 * boundaries. Accounts for flex gap so we don't falsely overflow and abandon
 * a half-filled page.
 */
export const buildPdfPages = (host, sourceRoot, metrics) => {
  const probe = document.createElement("div");
  probe.className = "pdf-root";
  probe.style.cssText = `width:${metrics.innerWidthPx}px;position:relative;`;
  injectPdfStyles(probe);
  host.appendChild(probe);

  const queue = [];
  collectBlocks(sourceRoot).forEach((b) => {
    b.parentNode?.removeChild(b);
    queue.push(b);
  });

  const pages = [];
  let page = createPageEl(metrics);
  injectPdfStyles(page);
  host.appendChild(page);
  pages.push(page);

  const startNewPage = () => {
    page = createPageEl(metrics);
    injectPdfStyles(page);
    host.appendChild(page);
    pages.push(page);
  };

  const remainingForNext = () => {
    const used = pageUsedHeight(page);
    const gap = contentChildren(page).length > 0 ? PARA_GAP_PX : 0;
    return metrics.pageHeightPx - used - gap;
  };

  while (queue.length) {
    const block = queue.shift();

    probe.appendChild(block);
    void probe.offsetHeight;
    const natural = blockExtent(block, probe.getBoundingClientRect().top).height;
    probe.removeChild(block);

    if (natural > metrics.pageHeightPx + 0.5) {
      const pieces = [];
      let current = block;
      while (current) {
        probe.appendChild(current);
        void probe.offsetHeight;
        const h = blockExtent(current, probe.getBoundingClientRect().top).height;
        if (h <= metrics.pageHeightPx + 0.5) {
          probe.removeChild(current);
          pieces.push(current);
          break;
        }
        probe.removeChild(current);
        const cont = splitBlockToHeight(current, metrics.pageHeightPx, probe);
        pieces.push(current);
        if (!cont) break;
        current = cont;
      }
      queue.unshift(...pieces);
      continue;
    }

    const remaining = remainingForNext();

    if (natural <= remaining + 0.5) {
      page.appendChild(block);
      continue;
    }

    // Fill this page with as many lines as fit, then continue on the next.
    if (remaining >= 20 && contentChildren(page).length > 0) {
      const continuation = splitBlockToHeight(block, remaining, probe);
      if (continuation) {
        page.appendChild(block);
        void page.offsetHeight;
        if (pageUsedHeight(page) <= metrics.pageHeightPx + 1) {
          queue.unshift(continuation);
          startNewPage();
          continue;
        }
        // Subpixel miss: keep existing page content, move both pieces next.
        page.removeChild(block);
        queue.unshift(continuation);
        queue.unshift(block);
        startNewPage();
        continue;
      }
    }

    if (contentChildren(page).length > 0) startNewPage();
    page.appendChild(block);
  }

  host.removeChild(probe);
  return pages;
};

/** @deprecated pads-based API — kept for older test imports; prefer buildPdfPages */
export const paginateHtmlForPdf = (container, pageHeightPx) => {
  return { pageHeightPx, minLines: MIN_LINES, paraGapPx: PARA_GAP_PX };
};

export const measurePdfWhitespace = (container, pageHeightPx) => {
  const containerTop = container.getBoundingClientRect().top;
  const totalHeight = container.getBoundingClientRect().height;
  const pageCount = Math.max(1, Math.ceil(totalHeight / pageHeightPx));
  const gaps = [];
  for (let page = 0; page < pageCount - 1; page++) {
    gaps.push({ page: page + 1, whitespacePx: 0 });
  }
  void containerTop;
  return gaps;
};

export const generatePDF = async (title, content) => {
  const metrics = getPdfPageMetrics();
  const { margin, scale, innerWidthPx, innerWidthMm, innerHeightMm } = metrics;

  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;left:-100000px;top:0;opacity:1;pointer-events:none;z-index:-1;";
  document.body.appendChild(host);

  const source = document.createElement("div");
  source.className = "pdf-root";
  source.style.width = `${innerWidthPx}px`;
  source.innerHTML = prepareHTMLForPDF(content);
  injectPdfStyles(source);
  host.appendChild(source);

  try {
    void source.offsetHeight;
    const pages = buildPdfPages(host, source, metrics);

    // Drop a trailing empty page if the last push created one unused.
    while (
      pages.length > 1 &&
      [...pages[pages.length - 1].children].every((c) => c.tagName === "STYLE")
    ) {
      const empty = pages.pop();
      empty.remove();
    }

    const pdf = new jsPDF({
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    });

    for (let i = 0; i < pages.length; i++) {
      const pageEl = pages[i];
      const canvas = await html2canvas(pageEl, {
        scale,
        useCORS: true,
        letterRendering: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        backgroundColor: "#FFFEFD",
        width: metrics.innerWidthPx,
        height: metrics.pageHeightPx,
        windowWidth: metrics.innerWidthPx,
        windowHeight: metrics.pageHeightPx,
      });

      if (!canvas || canvas.width < 2 || canvas.height < 2) {
        throw new Error(`PDF page ${i + 1} canvas was empty`);
      }

      const img = canvas.toDataURL("image/jpeg", 0.98);
      if (i > 0) pdf.addPage();
      pdf.addImage(
        img,
        "JPEG",
        margin[3],
        margin[0],
        innerWidthMm,
        innerHeightMm
      );
    }

    pdf.save(`${title || "document"}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
  } finally {
    if (document.body.contains(host)) {
      document.body.removeChild(host);
    }
  }
};

const csvEscape = (value) => {
  const text = value == null ? "" : String(value);
  if (/[;"\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const formatCivilite = (title) => {
  const normalized = String(title || "").toLowerCase();
  if (
    normalized === "male" ||
    normalized === "m" ||
    normalized === "homme" ||
    normalized === "mr" ||
    normalized === "m." ||
    normalized === "monsieur"
  ) {
    return "Monsieur";
  }
  if (
    normalized === "female" ||
    normalized === "f" ||
    normalized === "femme" ||
    normalized === "mme" ||
    normalized === "madame"
  ) {
    return "Madame";
  }
  return title || "";
};

const encodeLatin1 = (text) => {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    bytes[i] = code <= 0xff ? code : 0x3f;
  }
  return bytes;
};

const LA_POSTE_HEADERS = [
  "Nom de la société",
  "Identité du destinataire et/ou service",
  "Civilité (autorisée: monsieur ou madame)",
  "Nom",
  "Prénom",
  "*N° et libellé de voie",
  "*Code postal",
  "*Ville",
  "*Pays",
  "Lieu-dit/BP",
  "Bâtiment, résidence",
  "Appartement, chez, étage",
];

export const generateLaPosteCSV = (data = []) => {
  const rows = data.map((recipient) => [
    "",
    "",
    formatCivilite(recipient.title),
    recipient.lastName || "",
    recipient.firstName || "",
    recipient.stAndName || "",
    recipient.postalCode || "",
    recipient.city || "",
    recipient.country || "France",
    recipient.locality || "",
    recipient.address || "",
    "",
  ]);

  const lines = [LA_POSTE_HEADERS, ...rows].map((cols) =>
    cols.map(csvEscape).join(";")
  );
  const csvContent = `${lines.join("\r\n")}\r\n`;
  const blob = new Blob([encodeLatin1(csvContent)], {
    type: "text/csv;charset=iso-8859-1;",
  });
  saveAs(blob, "recipients.csv");
};

/** @deprecated Prefer generateLaPosteCSV for postal dispatch */
export const generateXLSX = (data) => {
  generateLaPosteCSV(data);
};
